import { defineStore } from "pinia";
import { ref, shallowRef, shallowReactive } from "vue";

import { log } from "@/log";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";

import { apiClient } from "@/client";
import {
	Communication,
	type CommunicationDTO,
	Contact,
	type ContactDTO,
	FieldseekerServiceRequest,
	type FieldseekerServiceRequestDTO,
	PublicReport,
	type PublicReportDTO,
	VirtualInspectionDetail,
} from "@/type/api";

interface uriHaver {
	uri: string;
}
type jsonConverter<dto, full> = (arg: dto) => full;
function createResourceStore<dto, full extends uriHaver>(
	resource_name: string,
	api_base: string,
	from_json: jsonConverter<dto, full>,
) {
	const byURI = shallowReactive<Map<string, full>>(new Map());
	// Number of list fetches currently in flight, so loadingAll() stays truthful.
	const _inflight = ref(0);
	// Every list query that has been loaded; "" stands for the bare, unfiltered
	// list. hasAll() means "at least one list has arrived", and a reconnect
	// replays exactly these queries.
	const _loadedQueries = ref(new Set<string>());
	const _resourceFetchByURI = shallowRef<Map<string, Promise<full> | null>>(
		new Map(),
	);

	// Subscription
	SSEManager.subscribe((msg: SSEMessageResource) => {
		if (msg.resource.startsWith(resource_name)) {
			if (msg.type == "created") {
				console.log("New resource", resource_name, msg.resource, msg.uri);
				fetchByURI(msg.uri);
			} else if (msg.type == "updated") {
				console.log("Updated resource", resource_name, msg.resource, msg.uri);
				fetchByURI(msg.uri);
			} else if (msg.type == "deleted") {
				console.log("Deleted resource", resource_name, msg.resource, msg.uri);
				byURI.delete(msg.uri);
			}
		}
	});
	SSEManager.subscribeReconnect(() => {
		log.info("SSE reconnected, triggering full refresh for", resource_name);
		// Refetch every list query that has been loaded, not just the bare list,
		// otherwise a slice fetched with a query goes stale after a reconnect.
		for (const query of [..._loadedQueries.value]) {
			fetchAll(query === "" ? undefined : query);
		}
	});

	async function byID(id: string): Promise<full> {
		const uri = uriFromID(id);
		return ensureURI(uri);
	}
	async function ensureURI(uri: string): Promise<full> {
		// Check if we already have the data
		const existing = byURI.get(uri);
		if (existing) {
			return existing;
		}

		// Check if we're already fetching it
		let fetchPromise = _resourceFetchByURI.value.get(uri);
		if (fetchPromise) {
			return fetchPromise;
		}

		// Start fetching
		fetchPromise = fetchByURI(uri);
		_resourceFetchByURI.value.set(uri, fetchPromise);

		try {
			const result = await fetchPromise;
			return result;
		} finally {
			// Clean up the promise after it resolves
			_resourceFetchByURI.value.delete(uri);
		}
	}
	async function fetchAll(query?: string): Promise<full[]> {
		/*
		const sessionStore = useSessionStore();
		const session = await sessionStore.get();
		const params = new URLSearchParams();
		params.append("sort", "-created");
		const url = `${session.urls.api.mailer}?${params}`;
	   */
		const key = query ?? "";
		const url = key === "" ? api_base : `${api_base}?${key}`;
		_inflight.value++;
		try {
			const dtos = (await apiClient.JSONGet(url)) as dto[];
			const resources = dtos.map((m: dto) => from_json(m));
			//let new_all = new Map<string, full>();
			resources.forEach((r: full) => {
				byURI.set(r.uri, r);
				//new_all.set(r.uri, r);
				log.info("fetchAll set", resource_name, r.uri);
			});
			//byURI.value = new_all;
			// Only a list that actually arrived counts as loaded; a failed fetch
			// leaves hasAll() false so the caller keeps showing its spinner.
			_loadedQueries.value.add(key);
			return resources;
		} finally {
			_inflight.value--;
		}
	}
	async function fetchByID(id: string): Promise<full> {
		const uri = uriFromID(id);
		return fetchByURI(uri);
	}
	async function fetchByURI(uri: string): Promise<full> {
		try {
			const response = await fetch(uri);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const body: dto = await response.json();
			const report = from_json(body);
			byURI.set(report.uri, report);
			log.info("fetchByURI set", resource_name, report.uri);
			return report;
		} catch (err) {
			log.error("Error loading resource by URI", resource_name, uri, err);
			throw err;
		}
	}
	// Install a resource the server already returned (e.g. a mark/mutation
	// response), so the UI updates without a round trip.
	function apply(resource: full): void {
		byURI.set(resource.uri, resource);
		log.info("apply set", resource_name, resource.uri);
	}
	function hasAll(): boolean {
		return _loadedQueries.value.size > 0;
	}
	function loadingAll(): boolean {
		return _inflight.value > 0;
	}
	function loadingURI(uri: string | undefined): boolean {
		if (uri === undefined) {
			return false;
		}
		return !!_resourceFetchByURI.value.get(uri);
	}
	function uriFromID(id: string): string {
		return `${api_base}/${id}`;
	}
	return {
		apiBase: api_base,
		apply,
		byID,
		byURI,
		ensureURI,
		fetchAll,
		fetchByID,
		fetchByURI,
		hasAll,
		loadingAll,
		loadingURI,
		resourceName: resource_name,
	};
}
export const useStoreResource = defineStore("resource", () => {
	const communication = createResourceStore<CommunicationDTO, Communication>(
		"sync:communication",
		"/api/communication",
		Communication.fromJSON,
	);
	const contact = createResourceStore<ContactDTO, Contact>(
		"sync:contact",
		"/api/contact",
		Contact.fromJSON,
	);
	const publicreport = createResourceStore<PublicReportDTO, PublicReport>(
		"sync:publicreport",
		"/api/publicreport",
		PublicReport.fromJSON,
	);
	const virtualinspection = createResourceStore<
		VirtualInspectionDetail,
		VirtualInspectionDetail
	>(
		"sync:virtualinspection",
		"/api/virtual-inspection",
		VirtualInspectionDetail.fromJSON,
	);
	const servicerequest = createResourceStore<
		FieldseekerServiceRequestDTO,
		FieldseekerServiceRequest
	>(
		"sync:fieldseeker-service-request",
		"/api/integration/fieldseeker/service-request",
		FieldseekerServiceRequest.fromJSON,
	);
	const all_resource_stores = [
		communication,
		contact,
		publicreport,
		servicerequest,
		virtualinspection,
	];
	async function fetch_(uri: string) {
		for (let i = 0; i < all_resource_stores.length; i++) {
			const resource_store = all_resource_stores[i];
			if (uri.includes(resource_store.apiBase)) {
				resource_store.fetchByURI(uri);
				return;
			}
		}
		log.error("URI does not match a resource store", uri);
	}
	return {
		communication: communication,
		contact: contact,
		fetch: fetch_,
		publicreport: publicreport,
		servicerequest: servicerequest,
		virtualinspection: virtualinspection,
	};
});
